import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaSearch, FaUsers, FaPlus, FaComments, FaHeartbeat, FaArrowLeft, FaClock, FaCheck, FaTimes, FaCamera, FaTrash, FaGlobeAmericas, FaLock, FaUserPlus, FaRocket
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import groupService from '../services/groupService';
import userService from '../services/userService';
import Nav from '../components/Nav';
import FeedbackModal from '../components/shared/FeedbackModal';
import './ResearcherGroups.css'; // Reusamos los estilos premium existentes

const PatientGroups = () => {
    const { user, token } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [activeGroups, setActiveGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [requests, setRequests] = useState([]);
    const [activeTab, setActiveTab] = useState('mis-grupos');
    const [searchQuery, setSearchQuery] = useState('');
    const [pendingJoinIds, setPendingJoinIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const [groupForm, setGroupForm] = useState({
        name: '',
        description: '',
        area: '',
        privacy: 'publico'
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'error'
    });

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

    useEffect(() => {
        if (token) {
            loadGroups();
        }
    }, [token]);

    const loadGroups = async () => {
        try {
            setLoading(true);
            const data = await groupService.getGroups(token);

            // Filtrar grupos por módulo (REDPATIENT)
            const communityGroups = data.filter(g => {
                const roles = g.creator?.user?.roles || [];
                return roles.some(role => role.module === 'REDPATIENT');
            });

            setAllGroups(communityGroups);

            // Mis grupos: donde soy creador (comparando ID de perfil)
            const myProfileId = user?.profile?.id;
            const myGroups = communityGroups.filter(g => g.creator?.id === myProfileId);
            setActiveGroups(myGroups);

            // Extraer todas las solicitudes pendientes de mis grupos
            const allPending = [];
            myGroups.forEach(group => {
                if (group.pendingRequests) {
                    group.pendingRequests.forEach(reqProfile => {
                        allPending.push({
                            id: reqProfile.id,
                            groupId: group.id,
                            profileId: reqProfile.id,
                            userId: reqProfile.userId || reqProfile.user?.id,
                            name: `${reqProfile.firstName} ${reqProfile.lastName}`,
                            role: 'Miembro',
                            group: group.name,
                            avatar: reqProfile.firstName?.charAt(0) || 'U'
                        });
                    });
                }
            });
            setRequests(allPending);

        } catch (err) {
            console.error('Error loading groups:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleAuthorize = async (requestId) => {
        const req = requests.find(r => r.id === requestId);
        if (!req) return;
        try {
            await groupService.approveJoinRequest(req.groupId, req.profileId, token);
            showFeedback('¡Aprobado!', 'El nuevo miembro ha sido unido al grupo.', 'success');
            loadGroups();
        } catch (err) {
            showFeedback('Error', 'No se pudo aprobar la solicitud.');
        }
    };

    const toggleGroupStatus = async (id, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'closed' : 'active';
        try {
            await groupService.toggleStatus(id, newStatus, token);
            loadGroups();
        } catch (err) {
            showFeedback('Error', 'No se pudo cambiar el estado del grupo.');
        }
    };

    const handleReject = async (requestId) => {
        // En un sistema real, aquí llamaríamos al servicio de rechazo
        setRequests(prev => prev.filter(r => r.id !== requestId));
        showFeedback('Solicitud eliminada', 'Se ha descartado la solicitud.');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setGroupForm(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = (e) => {
        e.stopPropagation();
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const showFeedback = (title, message, type = 'error') => {
        setModalConfig({ isOpen: true, title, message, type });
    };

    const handleCreateGroup = async () => {
        if (!groupForm.name) return showFeedback('Información Faltante', 'Ingresa un nombre para el grupo.');
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('name', groupForm.name);
            formData.append('description', groupForm.description);
            formData.append('area', groupForm.area);
            formData.append('privacy', groupForm.privacy);
            if (selectedImage) formData.append('image', selectedImage);

            await groupService.createGroup(formData, token);
            setShowModal(false);
            setGroupForm({ name: '', description: '', area: '', privacy: 'publico' });
            setSelectedImage(null);
            setImagePreview(null);
            showFeedback('¡Éxito!', 'Tu grupo de apoyo ha sido creado.', 'success');
            loadGroups();
        } catch (err) {
            showFeedback('Error', 'No se pudo crear el grupo.');
        } finally {
            setLoading(false);
        }
    };

    const handleJoinRequest = async (groupId) => {
        try {
            console.log('🚀 [JoinRequest] Intentando unirse al grupo:', groupId);
            setPendingJoinIds(prev => [...prev, groupId]);
            await groupService.requestJoin(groupId, token);
            showFeedback('Solicitud Enviada', 'Tu solicitud ha sido enviada al administrador del grupo.', 'success');
        } catch (err) {
            console.error('❌ [JoinRequest] Error al unirse:', err.response?.data || err.message);
            showFeedback('Error', 'No se pudo procesar la solicitud: ' + (err.response?.data?.msg || err.message));
        }
    };

    return (
        <div className="rg-page full-width patient-groups">
            <header className="rg-top-header">
                <div className="rg-header-left">
                    <button className="rg-back-btn" onClick={() => navigate('/community-feed')}>
                        <FaArrowLeft /> Regresar
                    </button>
                    <div className="rg-header-logo">
                        <img src="/logoecseia.png" alt="Logo" className="rg-logo-img-small" />
                        <span className="rg-logo-text-small">
                            <span className="rg-eco">Eco</span>Science<strong>Innova</strong>
                        </span>
                    </div>
                </div>
                <div className="rg-header-center">
                    <div className="rg-search-bar">
                        <FaSearch className="rg-search-icon" />
                        <input
                            type="text"
                            placeholder="Buscar comunidades y grupos de apoyo..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
                <div className="rg-header-right">
                    <div className="rg-user-identity">
                        <div className="rg-user-details">
                            <span className="rg-user-name-top">{user?.profile?.firstName} {user?.profile?.lastName}</span>
                            <span className="rg-user-role-top">Comunidad Paciente</span>
                        </div>
                    </div>
                </div>
            </header>

            <div className="rg-main-container">
                <div className="rg-scroll">
                    <section className="rg-hero">
                        <div>
                            <h1>Grupos y Comunidades</h1>
                            <p>Encuentra apoyo, comparte experiencias y conéctate con personas en situaciones similares.</p>
                        </div>
                        <button className="rg-create-btn" onClick={() => setShowModal(true)}>
                            <FaPlus /> Crear Nueva Comunidad
                        </button>
                    </section>

                    <div className="rg-tabs">
                        <button className={`rg-tab-btn ${activeTab === 'mis-grupos' ? 'active' : ''}`} onClick={() => setActiveTab('mis-grupos')}>
                            <FaUsers /> Mis Grupos
                        </button>
                        <button className={`rg-tab-btn ${activeTab === 'solicitudes' ? 'active' : ''}`} onClick={() => setActiveTab('solicitudes')}>
                            <FaUserPlus /> Solicitudes
                            {requests.length > 0 && <span className="rg-tab-count">{requests.length}</span>}
                        </button>
                        <button className={`rg-tab-btn ${activeTab === 'buscar' ? 'active' : ''}`} onClick={() => setActiveTab('buscar')}>
                            <FaSearch /> Explorar Comunidades
                        </button>
                    </div>

                    {activeTab === 'mis-grupos' && (
                        <section className="rg-section">
                            <div className="rg-sec-header"><h2><FaUsers className="rg-sec-icon" /> Mis Comunidades</h2></div>
                            <div className="rg-cards-grid">
                                {activeGroups
                                    .filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                    .map(g => (
                                        <div key={g.id} className="rg-card">
                                            <div className="rg-card-top">
                                                <div className="rg-card-icon-img">
                                                    {g.imageUrl ? <img src={`${backendUrl}${g.imageUrl}`} alt={g.name} className="rg-group-img" /> : <FaHeartbeat />}
                                                </div>
                                                <span className={`rg-badge ${g.status}`}>{g.status?.toUpperCase() || 'ACTIVO'}</span>
                                            </div>
                                            <h3>{g.name}</h3>
                                            <p className="rg-card-desc">{g.description}</p>
                                            <div className="rg-card-meta">
                                                <div><span className="rg-meta-label">MIEMBROS</span><span className="rg-meta-val">{g.members?.length || 0} personas</span></div>
                                                <div><span className="rg-meta-label">TIPO</span><span className="rg-meta-val">{g.area || 'Apoyo General'}</span></div>
                                            </div>

                                            <button
                                                className={`rg-status-toggle ${g.status === 'active' ? 'to-close' : 'to-open'}`}
                                                style={{ width: '100%', marginTop: '12px' }}
                                                onClick={() => toggleGroupStatus(g.id, g.status || 'active')}
                                            >
                                                {g.status === 'active' ? 'Cerrar Grupo' : 'Activar Grupo'}
                                            </button>
                                        </div>
                                    ))}
                                {activeGroups.length === 0 && <p className="rg-empty-text">Aún no te has unido a ninguna comunidad.</p>}
                            </div>
                        </section>
                    )}

                    {/* Solicitudes de Unión */}
                    {activeTab === 'solicitudes' && (
                        <section className="rg-section">
                            <div className="rg-sec-header">
                                <h2><FaUserPlus className="rg-sec-icon" /> Solicitudes de Unión</h2>
                            </div>
                            <div className="rg-requests-list">
                                {requests.length > 0 ? requests.map(req => (
                                    <div key={req.id} className="rg-request-row">
                                        <div className="rg-req-left">
                                            <div className="rg-req-avatar">{req.avatar}</div>
                                            <div>
                                                <h4>{req.name}</h4>
                                                <p>Solicitud para unirse a: <strong>{req.group}</strong></p>
                                            </div>
                                        </div>
                                        <div className="rg-req-actions">
                                            <button className="rg-btn-approve" onClick={() => handleAuthorize(req.id)}><FaCheck /> Aprobar</button>
                                            <button className="rg-btn-reject" onClick={() => handleReject(req.id)}><FaTimes /></button>
                                        </div>
                                    </div>
                                )) : (
                                    <p className="rg-empty-text">No hay solicitudes pendientes.</p>
                                )}
                            </div>
                        </section>
                    )}

                    {activeTab === 'buscar' && (
                        <section className="rg-section">
                            <div className="rg-sec-header"><h2><FaGlobeAmericas className="rg-sec-icon" /> Descubrir Comunidades</h2></div>
                            <div className="rg-discover-list">
                                {allGroups
                                    .filter(g => g.name.toLowerCase().includes(searchQuery.toLowerCase()))
                                    .map(g => (
                                        <div key={g.id} className="rg-discover-row">
                                            <div className="rg-disc-left">
                                                <div className="rg-disc-icon-img">
                                                    {g.imageUrl ? <img src={`${backendUrl}${g.imageUrl}`} alt={g.name} className="rg-group-img-small" /> : <FaUsers />}
                                                </div>
                                                <div>
                                                    <h4>{g.name}</h4>
                                                    <span className="rg-disc-meta">{g.members?.length || 0} miembros · {g.area}</span>
                                                    <p className="rg-disc-desc-mini">{g.description?.substring(0, 100)}...</p>
                                                </div>
                                            </div>
                                            {pendingJoinIds.includes(g.id) ? (
                                                <button className="rg-join-btn pending" disabled><FaClock /> Solicitado</button>
                                            ) : (
                                                <button className="rg-join-btn" onClick={() => handleJoinRequest(g.id)}>Unirse al Grupo</button>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </section>
                    )}
                </div>
            </div>

            {/* Modal de Creación */}
            {showModal && (
                <div className="rg-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="rg-modal" onClick={e => e.stopPropagation()}>
                        <div className="rg-modal-header">
                            <div className="rg-modal-title-row">
                                <div className="rg-modal-icon"><FaPlus /></div>
                                <div>
                                    <h3>Crear Nueva Comunidad</h3>
                                    <p>Conecta con personas que comparten tus mismos retos</p>
                                </div>
                            </div>
                            <button className="rg-modal-close" onClick={() => setShowModal(false)}><FaTimes /></button>
                        </div>
                        <div className="rg-modal-body">
                            <div className="rg-group-image-upload">
                                <div className="rg-image-preview-box" onClick={() => fileInputRef.current.click()}>
                                    {imagePreview ? (
                                        <><img src={imagePreview} alt="Preview" className="rg-image-preview" /><button className="rg-btn-remove-image" onClick={handleRemoveImage}><FaTrash /></button></>
                                    ) : (
                                        <div className="rg-image-placeholder"><FaCamera /><span>Imagen del grupo</span></div>
                                    )}
                                </div>
                                <input type="file" ref={fileInputRef} onChange={handleImageChange} style={{ display: 'none' }} accept="image/*" />
                            </div>
                            <label className="rg-modal-label">Nombre de la comunidad</label>
                            <input type="text" name="name" value={groupForm.name} onChange={handleInputChange} className="rg-modal-input" placeholder="Ej. Apoyo para Diabetes Tipo 1" />
                            <label className="rg-modal-label">Descripción</label>
                            <textarea name="description" value={groupForm.description} onChange={handleInputChange} className="rg-modal-textarea" placeholder="¿De qué trata este grupo?" rows={4} />
                        </div>
                        <div className="rg-modal-footer">
                            <button className="rg-modal-cancel" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="rg-modal-submit" onClick={handleCreateGroup} disabled={loading}>{loading ? 'Creando...' : <><FaRocket /> Crear Grupo</>}</button>
                        </div>
                    </div>
                </div>
            )}

            <FeedbackModal
                isOpen={modalConfig.isOpen}
                onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
                title={modalConfig.title}
                message={modalConfig.message}
                type={modalConfig.type}
            />
        </div>
    );
};

export default PatientGroups;
